"use client";

import { Category } from "@/dto/category";
import { DayPart, DayPartMenuCategories } from "@/dto/dayPartMenu";
import { TableData } from "@/dto/tableData";
import pom from "@/utils/pom";
import { getFromLocalStorage } from "@/utils/localStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { saveToSessionStorage as saveSessionValue } from "@/utils/sessionStorageUtils";
import { toastService } from "@/utils/toastService";
import { useEffect } from "react";
import { RECOMMENDED_CATEGORY_ID } from "@/constants/itemMarkers";

interface UseNavigationBarEffectsParams {
  pathName: string | null;
  isScanToOrderFlow: boolean;
  scanToOrderInfo: unknown;
  setSearchFieldOpen: (open: boolean) => void;
  setSearchQuery: (query: string) => void;
  setIsRetailCartOpen: (open: boolean) => void;
  setIsRestaurantCartOpen: (open: boolean) => void;
  setIsPreview: (preview: boolean) => void;
  setTableData: (data: TableData | null) => void;
  setAddedToCartBannerShowing: (showing: boolean) => void;
  navigationCategories: { length: number } | undefined;
  remainingCategories: Category[];
  mappedCategories: Category[];
  selectedMenu: DayPart | null;
  dayPartMenuCategories: DayPartMenuCategories[] | undefined;
  setMenuState: (categories: Category[]) => void;
  isUserInitiatedDayPartSwitch: { current: boolean };
}

/** Owns browser/event effects used by the navigation shell. */
export const useNavigationBarEffects = ({
  pathName,
  isScanToOrderFlow,
  scanToOrderInfo,
  setSearchFieldOpen,
  setSearchQuery,
  setIsRetailCartOpen,
  setIsRestaurantCartOpen,
  setIsPreview,
  setTableData,
  setAddedToCartBannerShowing,
  navigationCategories,
  remainingCategories,
  mappedCategories,
  selectedMenu,
  dayPartMenuCategories,
  setMenuState,
  isUserInitiatedDayPartSwitch,
}: UseNavigationBarEffectsParams): void => {
  useEffect(() => {
    const timeout = setTimeout(() => {
      setAddedToCartBannerShowing(false);
    }, 3000);
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === LocalStorageKey.CURRENT_CART_EVENT) {
        toastService.notifyItemAdded();
      }
    };
    globalThis.addEventListener("storage", handleStorageChange);
    return () => {
      globalThis.removeEventListener("storage", handleStorageChange);
      clearTimeout(timeout);
    };
  }, [setAddedToCartBannerShowing]);

  useEffect(() => {
    setSearchFieldOpen(false);
    setSearchQuery("");
    if (globalThis.location.href?.includes("#cart")) {
      setIsRetailCartOpen(true);
    }
    if (globalThis.location.href?.includes("#restaurant-cart")) {
      setIsRestaurantCartOpen(true);
    }
    if (
      isScanToOrderFlow &&
      !globalThis.location.href?.includes("#restaurant-cart")
    ) {
      setIsRestaurantCartOpen(false);
    }
  }, [pathName]);

  useEffect(() => {
    setIsPreview(globalThis.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const tableData = getFromLocalStorage<TableData | undefined>(
        LocalStorageKey.TABLE_DATA,
        undefined
      );
      if (tableData && scanToOrderInfo) {
        setTableData(tableData);
        if (tableData?.order?.id) {
          saveSessionValue(SessionStorageKey.RECEIPT_ID, tableData.order.id);
        }
      } else {
        setTableData(null);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const hasRecommendedCategory = mappedCategories.some(
      ({ id }) => id === RECOMMENDED_CATEGORY_ID
    );
    if (
      !hasRecommendedCategory &&
      navigationCategories?.length === 5 &&
      remainingCategories.length === 1
    ) {
      const promotedCategory = remainingCategories.shift();
      if (promotedCategory) mappedCategories.push(promotedCategory);
    }
  }, [navigationCategories, remainingCategories, mappedCategories]);

  useEffect(() => {
    if (!selectedMenu) return;
    const categories =
      dayPartMenuCategories?.find((item) => item.menuId === selectedMenu.menuId)
        ?.categories || [];
    setMenuState(categories);

    if (!isUserInitiatedDayPartSwitch.current) return;

    const observer = new MutationObserver((_mutations, mutationObserver) => {
      const firstSecondaryNavOption = document.getElementById(
        pom.accessibilityIds.secondaryNavStart
      );
      if (firstSecondaryNavOption) {
        firstSecondaryNavOption.focus();
        mutationObserver.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(() => {
      isUserInitiatedDayPartSwitch.current = false;
      observer.disconnect();
    }, 5000);

    return () => {
      observer.disconnect();
      clearTimeout(timeoutId);
    };
  }, [selectedMenu]);
};
